import { monitoringFormV1Base } from "./formBuilderTemplates/136/monitoringForm.js";
import { s136ExtensionFormV1Base } from "./formBuilderTemplates/136/s136ExtensionForm.js";
import { a1V1Base } from "./formBuilderTemplates/a1.js";
import { a2V1Base } from "./formBuilderTemplates/a2.js";
import { a3V1Base } from "./formBuilderTemplates/a3.js";
import { a4V1Base } from "./formBuilderTemplates/a4.js";
import { a5V1Base } from "./formBuilderTemplates/a5.js";
import { a6V1Base } from "./formBuilderTemplates/a6.js";
import { a7V1Base } from "./formBuilderTemplates/a7.js";
import { a8V1Base } from "./formBuilderTemplates/a8.js";
import { a9V1Base } from "./formBuilderTemplates/a9.js";
import { a10V1Base } from "./formBuilderTemplates/a10.js";
import { a11V1Base } from "./formBuilderTemplates/a11.js";
import { acdV1Base } from "./formBuilderTemplates/acd/acd.js";
import { cto1V1Base } from "./formBuilderTemplates/cto1.js";
import { cto2V1Base } from "./formBuilderTemplates/cto2.js";
import { cto3V1Base } from "./formBuilderTemplates/cto3.js";
import { cto4V1Base } from "./formBuilderTemplates/cto4.js";
import { cto5V1Base } from "./formBuilderTemplates/cto5.js";
import { cto6V1Base } from "./formBuilderTemplates/cto6.js";
import { cto7V1Base } from "./formBuilderTemplates/cto7.js";
import { cto8V1Base } from "./formBuilderTemplates/cto8.js";
import { cto9V1Base } from "./formBuilderTemplates/cto9.js";
import { cto10V1Base } from "./formBuilderTemplates/cto10.js";
import { cto12V1Base } from "./formBuilderTemplates/cto12.js";
import { hpdV1Base } from "./formBuilderTemplates/discharge/hpd.js";
import { s23V1Base } from "./formBuilderTemplates/discharge/s23.js";
import { h1V1Base } from "./formBuilderTemplates/h1.js";
import { h2V1Base } from "./formBuilderTemplates/h2.js";
import { h3V1Base } from "./formBuilderTemplates/h3.js";
import { h4V1Base } from "./formBuilderTemplates/h4.js";
import { h5V1Base } from "./formBuilderTemplates/h5.js";
import { h6V1Base } from "./formBuilderTemplates/h6.js";
import { m1V1Base } from "./formBuilderTemplates/m1.js";
import { m2V1Base } from "./formBuilderTemplates/m2.js";
import { bidV1Base } from "./formBuilderTemplates/mca/bid.js";
import { fdmcaV1Base } from "./formBuilderTemplates/mca/fdmca.js";
import { s62V1Base } from "./formBuilderTemplates/s62.js";
import { soadCTO11V1Base } from "./formBuilderTemplates/soad/soad-cto11.js";
import { soadNoCertV1Base } from "./formBuilderTemplates/soad/soad-nocert.js";
import { soadT2V1Base } from "./formBuilderTemplates/soad/soad-t2.js";
import { soadT3V1Base } from "./formBuilderTemplates/soad/soad-t3.js";
import { soadT4V1Base } from "./formBuilderTemplates/soad/soad-t4.js";
import { soadT5V1Base } from "./formBuilderTemplates/soad/soad-t5.js";
import { soadT6V1Base } from "./formBuilderTemplates/soad/soad-t6.js";
import { soadReportFormV1Base } from "./formBuilderTemplates/soad/srf.js";
import { t2V1Base } from "./formBuilderTemplates/t2.js";
import { t4V1Base } from "./formBuilderTemplates/t4.js";
import { applicationTemplateIds, medicalRecommendationTemplateIds, } from "./sections.js";
import { hasIntersection } from "./util/predicates.js";
// True when the requirement does not require a registration number, or when the
// user holds a matching accreditation with its number populated (doctor → gmcNumber,
// nurse → pinNumber; other matching types have no number gate). Note: when
// requiresRegistrationNumber is set but `accreditation` is empty/unset, nothing
// matches and this returns false (fail-closed) — intentional.
export const meetsRegistrationNumberRequirement = (accreditations, requirement) => !requirement.requiresRegistrationNumber ||
    accreditations.some((a) => {
        if (!requirement.accreditation?.includes(a.type))
            return false;
        if (a.type === "doctor")
            return !!a.gmcNumber;
        if (a.type === "nurse")
            return !!a.pinNumber;
        return true;
    });
export const doesUserMeetRequirement = (user, requirement) => {
    // Accreditations
    const userAccreditationTypes = [
        ...user.accreditations.map((acc) => acc.type),
        // Everyone in an org with ACD enabled can do an ACD for now, as this feels like the least disgusting hack;
        "acd-facilitator",
    ];
    const hasProhibitedAccreditation = hasIntersection(requirement.prohibitedAccreditations, userAccreditationTypes);
    const hasValidAccreditation = hasIntersection(requirement.accreditation, userAccreditationTypes);
    // Check that registration numbers are present if required
    const hasValidRegistrationNumber = meetsRegistrationNumberRequirement(user.accreditations, requirement);
    // Team types
    const userTeamTypes = user.memberships.map((m) => m.teamType).filter(Boolean);
    const hasValidTeam = hasIntersection(requirement.teamType, userTeamTypes);
    const hasProhibitedTeam = hasIntersection(requirement.prohibitedTeamTypes, userTeamTypes);
    // When a non-empty allOf is present the positive gate is the conjunction of
    // every sub-requirement (each still OR-within-itself); top-level prohibited*
    // checks still apply alongside it. An empty/absent allOf falls back to the
    // normal OR gate (so allOf: [] never vacuously grants access via [].every()).
    const positiveMet = requirement.allOf && requirement.allOf.length > 0
        ? requirement.allOf.every((r) => doesUserMeetRequirement(user, r))
        : hasValidAccreditation || hasValidTeam;
    return (positiveMet &&
        hasValidRegistrationNumber &&
        !hasProhibitedAccreditation &&
        !hasProhibitedTeam);
};
// Who can SEE a form and INVITE others to start it. Falls back to the signing
// requirement when a part does not specify a broader view/invite gate, so all
// existing forms are unchanged.
export const getViewAndInviteRequirement = (part) => part.signing.viewAndInviteRequirement ?? part.signing.userOrTeamRequirement;
export const baseTemplates = [
    a1V1Base,
    a2V1Base,
    a3V1Base,
    a4V1Base,
    a5V1Base,
    a6V1Base,
    a7V1Base,
    a8V1Base,
    a9V1Base,
    a10V1Base,
    a11V1Base,
    cto1V1Base,
    cto2V1Base,
    cto4V1Base,
    cto5V1Base,
    cto10V1Base,
    cto7V1Base,
    cto3V1Base,
    cto6V1Base,
    cto12V1Base,
    cto9V1Base,
    cto8V1Base,
    h1V1Base,
    h2V1Base,
    h3V1Base,
    h4V1Base,
    h5V1Base,
    h6V1Base,
    s62V1Base,
    t2V1Base,
    t4V1Base,
    acdV1Base,
    s23V1Base,
    hpdV1Base,
    m1V1Base,
    m2V1Base,
    fdmcaV1Base,
    bidV1Base,
    monitoringFormV1Base,
    s136ExtensionFormV1Base,
    soadReportFormV1Base,
    soadT2V1Base,
    soadT3V1Base,
    soadT4V1Base,
    soadT5V1Base,
    soadT6V1Base,
    soadCTO11V1Base,
    soadNoCertV1Base,
];
export const formUploadBaseTemplates = [
    a1V1Base,
    a2V1Base,
    a3V1Base,
    a4V1Base,
    a5V1Base,
    a6V1Base,
    a7V1Base,
    a8V1Base,
    a9V1Base,
    a10V1Base,
    a11V1Base,
];
export const nonAdmissionTemplates = baseTemplates.filter((t) => !applicationTemplateIds.includes(t.id) &&
    !medicalRecommendationTemplateIds.includes(t.id));
export function getBaseFormTemplate(idOrObject, versionArg) {
    const id = idOrObject?.formTemplate?.id ??
        idOrObject?.id ??
        idOrObject ??
        null;
    const v = versionArg ??
        idOrObject?.version ??
        idOrObject?.formTemplate?.version ??
        null;
    return (baseTemplates.find((t) => t.id === id && (!v || t.version === v)) || null);
}
export function acknowledgedFormTemplateFor(id) {
    return (baseTemplates.find((template) => template.linkableForms?.some((lf) => lf.templateId === id)) || null);
}
//# sourceMappingURL=formTemplates.js.map